"use client"

import { useEffect, useRef, useState } from 'react'
import Layout from '@components/layout/Main'
import BreadcrumbBar from '@components/breadcrumbs/BreadcrumbBarNew'
import MapView from '@components/map/Map'
import SvgIcon from '@components/shared/svg-icons/SvgIcon'
import useT from '@hooks/useTranslation'
import Seo from 'src/seo/components/Seo'
import JsonLdSalon from 'src/seo/jsonld/JsonLdSalon'
import pluralize from '@utils/grammar/pluralize'
import salonLink from '@utils/links/salonLink'
import salonLogo from '@utils/links/salonLogo'
import mix from '@utils/styles/mix'
import { notify } from 'lib/notice/notice'
import { FIRM_BASE_PAGE } from '@constants/routes'
import { ContactBar, EmailReveal, PhoneReveal } from './SalonContact'
import SalonOffer from './SalonOffer'
import SalonWrite from './SalonWrite'

import offerStyles from 'src/pagesComponents/display/v2/Offer.module.css'
import oldStyles from '../SalonComponent.module.css'
import styles from './Salon.module.css'

const HOST = process.env.NEXT_PUBLIC_HOST
const OFFERS = ['ogłoszenie', 'ogłoszenia', 'ogłoszeń']
const DESC_CLAMP = 600

const initials = (name) => String(name || '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()

// Logo w dowolnych proporcjach: pole stałej wysokości; prawie kwadratowe — w ramce (proporcje znane po wczytaniu).
function Logo ({ firm }) {
    const [square, setSquare] = useState(false)
    const [failed, setFailed] = useState(false)
    const ref = useRef(null)
    const measure = (img) => setSquare(img.naturalWidth / (img.naturalHeight || 1) < 1.4)
    // obraz wczytany (albo niewczytany) przed hydratacją — bez zdarzeń load/error
    useEffect(() => {
        const img = ref.current
        if (img?.complete) img.naturalWidth ? measure(img) : setFailed(true)
    }, [])
    if (!firm.hasLogo || failed) return <div aria-hidden="true" className={styles.mono}>{initials(firm.name)}</div>
    return (
        <div className={styles.logo} data-square={square || undefined}>
            <img alt={`Logo: ${firm.name}`} decoding="async" onError={() => setFailed(true)} onLoad={e => measure(e.currentTarget)} ref={ref} src={salonLogo(firm._id)} />
        </div>
    )
}

function Share ({ firm }) {
    const share = async (e) => {
        const url = salonLink(firm.subdomain)
        const opener = e.currentTarget
        if (navigator.share) { navigator.share({ title: firm.name, url }).catch(() => null); return }
        try { await navigator.clipboard.writeText(url); notify('Link do wizytówki skopiowany.', { opener }) } catch (err) { notify(url, { opener }) }
    }
    return <button className={styles.btnLine} onClick={share} type="button"><SvgIcon name="share" />Udostępnij</button>
}

function Header ({ firm, total, write }) {
    const address = [firm.street, firm.city && !firm.street.toLowerCase().includes(firm.city.toLowerCase()) ? firm.city : ''].filter(Boolean).join(', ')
    return (
        <section aria-labelledby="firmaH" className={styles.head}>
            <div className={styles.identity}>
                <Logo firm={firm} />
                <h1 className={styles.name} id="firmaH">{firm.name || firm.legal}</h1>
                <ul className={styles.facts}>
                    { address && <li><SvgIcon name="pin" size="sm" /><span>{address}</span></li> }
                    <li><SvgIcon name="car" size="sm" />{ total ? <a href="#oferta">{total} {pluralize(total, OFFERS)}</a> : <span>Brak aktywnych ogłoszeń</span> }</li>
                    { firm.since && <li><SvgIcon name="cal" size="sm" /><span>Na samochody.pl od {firm.since}</span></li> }
                </ul>
                <div className={styles.acts}>
                    { firm.facebook && <a className={styles.btnLine} href={firm.facebook} rel="nofollow noopener noreferrer" target="_blank"><SvgIcon name="facebook" />Facebook<span className={styles.srOnly}> (otwiera się w nowej karcie)</span></a> }
                    <Share firm={firm} />
                    { firm.hasEmail && <EmailReveal id={firm._id} variant="mob" /> }
                </div>
            </div>
            <div aria-labelledby="kontaktH" className={styles.contact} id="kontakt" role="group">
                <p className={styles.contactTitle} id="kontaktH">Kontakt z firmą</p>
                <PhoneReveal id={firm._id} />
                { write }
                { firm.hasEmail && <EmailReveal id={firm._id} variant="desk" /> }
                <p className={styles.note}>Powołaj się na samochody.pl, gdy dzwonisz.</p>
            </div>
        </section>
    )
}

function About ({ html }) {
    const long = html.replace(/<[^>]+>/g, '').length > DESC_CLAMP
    const [open, setOpen] = useState(false)
    return (
        <section aria-labelledby="oFirmieH" className={styles.box} id="o-firmie">
            <h2 id="oFirmieH">O firmie</h2>
            <div className={mix([styles.desc, long && !open && styles.clamped])} dangerouslySetInnerHTML={{ __html: html }} id="opisFirmy" />
            { long && <button aria-controls="opisFirmy" aria-expanded={open} className={styles.more} onClick={() => setOpen(!open)} type="button">{open ? 'Zwiń opis' : 'Pokaż cały opis'}</button> }
        </section>
    )
}

// Mapa po zbliżeniu do widoku (jak na stronie ogłoszenia).
function Location ({ firm, map }) {
    const address = [firm.street, firm.city].filter(Boolean).join(', ')
    if (!map && !address) return null
    return (
        <section aria-labelledby="lokalizacjaH" className={styles.box} id="lokalizacja">
            <h2 id="lokalizacjaH">Lokalizacja</h2>
            { map && <div className={styles.map}><MapView coordinates={[map.lng, map.lat]} height={300} marker={map.kind === 'point'} radius={map.radius / 1000} zoom={11} /></div> }
            <div className={styles.address}>
                <strong>{firm.name || firm.legal}</strong>
                { address && <span>{address}</span> }
            </div>
        </section>
    )
}

export default function SalonPage ({ firm, map, offers, seo, visitor }) {
    const name = firm.name || firm.legal
    const write = {
        header: <SalonWrite className={mix([offerStyles.btnSecondary, offerStyles.btnBlock, styles.writeButton])} firm={firm} visitor={visitor} />,
        bar: <SalonWrite className={offerStyles.btnSecondary} firm={firm} label="Napisz" short visitor={visitor} />,
        empty: <SalonWrite className={offerStyles.btnPrimary} firm={firm} visitor={visitor} />,
    }
    return (
        <Layout logoonly navdown nofooterdiv>
            <Seo seo={seo} />
            <JsonLdSalon firm={firm} map={map} offers={offers} />
            <Layout.NavSpace />
            <div className={oldStyles.breadcrumb}>
                <BreadcrumbBar explicit={[{ name: useT('Baza firm'), path: HOST + FIRM_BASE_PAGE }, { name }]} microdata={false} style={{ gap: 16 }} />
            </div>
            <div className={styles.page}>
                <Header firm={firm} total={offers.total} write={write.header} />
                <nav aria-label="Sekcje wizytówki" className={styles.sections}>
                    <a href="#oferta">Oferta{offers.total ? ` (${offers.total})` : ''}</a>
                    { firm.description && <a href="#o-firmie">O firmie</a> }
                    { (map || firm.street || firm.city) && <a href="#lokalizacja">Lokalizacja</a> }
                </nav>
                <SalonOffer firm={firm} offers={offers} visitor={visitor} write={write.empty} />
                <div className={styles.two}>
                    { firm.description && <About html={firm.description} /> }
                    <Location firm={firm} map={map} />
                </div>
            </div>
            <ContactBar id={firm._id} write={write.bar} />
        </Layout>
    )
}
